import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/lib/api';
import { api } from '@/lib/api';
import type {
  RevenueProductCycle,
  RevenueProductQuery,
  RevenueProductRow,
  RevenueTaxMeasure,
} from '@/lib/types';
import { REQUESTED_TAX_MEASURE, measureOfResponse } from './revenue-product';

/**
 * The read behind the **Revenue by Product** tab.
 *
 * ⚠️ **ONE REQUEST, NEVER A PAGED LOOP.** `GET /revenue-tracker/products` has
 * no pager and `page`/`perPage` are a 422 — the row set is bounded by the
 * catalogue (bands × products × currencies) rather than by the data, so
 * `meta.total` is `data.length` and nothing is ever truncated. This is the one
 * place the tab differs materially from `useRevenueAnalytics`, which has to
 * follow two pages of companies to avoid dropping 22 of them.
 *
 * ── 🔴 TWO THINGS GO TO THE SERVER: THE SEARCH, AND THE BASE CURRENCY ──────
 *
 * ⚠️ *SUPERSEDED 2026-09-14: "THE SEARCH IS THE ONLY FILTER THAT GOES TO THE
 * SERVER".* **The currency did too**, the day it stopped being a filter and
 * became a base to convert into — see {@link useRevenueProducts}. Everything
 * else on the tab still narrows what came back.
 *
 * **A row is `(billing region, product)` — and, unconverted, a currency too —
 * and carries no company at all**, so a company search cannot be applied in the
 * browser: there is nothing here to match. It is therefore a query parameter.
 * That split is stated on screen, because the two behave differently under a
 * slow connection and a reader who assumes one is looking at the other will
 * misread a stale table.
 *
 * ⚠️ **THEREFORE THE SEARCH IS DEBOUNCED AND EVERY IN-FLIGHT READ IS ABORTED.**
 * A character-by-character search box on a shared office NAT is how a rate limit
 * gets found; and without the abort, two responses can land out of order and
 * the table settles on the results of a term the reader has already changed.
 *
 * ⚠️ **THE BASE CURRENCY IS NOT DEBOUNCED, AND THE DEBOUNCE IS KEYED ON THE
 * TERM RATHER THAN ON "IS THIS THE FIRST READ".** It is a click on three
 * segments, not typing, so there is no burst to absorb — and a third of a second
 * of skeletons after a deliberate press reads as a control that did not take.
 * The abort still applies: two currencies raced is the same out-of-order landing
 * as two search terms, one unit apart.
 */

/**
 * Long enough that a typed word is one request, short enough that the table
 * does not feel detached from the box. The same order as `use-kanban`'s.
 */
const DEBOUNCE_MS = 300;

export interface RevenueProductsState {
  rows: RevenueProductRow[];
  /**
   * Every cycle, **in date order**.
   *
   * ⚠️ **SORTED BY `startDate`, NEVER BY `id`.** Measured: id 11 is the earliest
   * cycle and id 1 the fourth. The id sequence has gaps and says nothing about
   * time, so ordering by it scrambles every column heading and every
   * year-on-year comparison — while looking entirely plausible.
   *
   * ⚠️ Compared as STRINGS. `YYYY-MM-DD` sorts lexicographically and is exact,
   * where `new Date()` attaches this browser's offset to a value that has none
   * and can move a boundary date across a day.
   */
  cycles: RevenueProductCycle[];
  /** True while a request is in flight, including a refresh over rows already shown. */
  loading: boolean;
  /** The server's own sentence, or `null`. */
  error: string | null;
  /**
   * Paid lines the server could not place in any cycle.
   *
   * ⚠️ **RENDERED WHENEVER IT IS ABOVE ZERO.** It is the only thing that can say
   * the columns do not sum to the business's revenue.
   */
  unmatchedLines: number;
  /**
   * 🔴 **THE UNIT THE SERVER SAYS IT MEASURED IN — `meta.measuredInCurrency`,
   * THE ECHO, NOT THE ONE THAT WAS ASKED FOR.** `null` is *this response was not
   * converted*: a server predating the key (`undefined` on the wire), or a
   * request that sent no base. The two are folded here because both mean *there
   * is no conversion to label*, and neither is a fault.
   *
   * ⛔ **NAMED FOR THE ANSWER RATHER THAN FOR THE QUESTION, DELIBERATELY —
   * exactly as the wire is.** The request key is `baseCurrency` and the echo is
   * not, because what a caller asked for and what the server measured in are two
   * facts, and the one failure this whole contract is shaped around is a caller
   * labelling figures from what it sent. A server that ignores the key answers a
   * perfectly ordinary `200` with USD money in it; printing `GBP` over that is
   * not a degraded render, it is a false statement about money.
   */
  convertedTo: string | null;
  /**
   * Paid lines with **no rate stored for their own bill date**, therefore in
   * none of the figures.
   *
   * 🔴 **`null` IS "NOTHING WAS EXAMINED" AND IS NOT `0`.** On the unconverted
   * path the server priced nothing, so it reports `null` rather than a zero that
   * would claim **nothing was lost** about a question nobody asked. ⛔ **A
   * `?? 0` anywhere between the wire and the render turns *we did not look* into
   * *we looked and everything was fine*, silently, on the one field that can say
   * a revenue report is short.** The render decides what an absence looks like;
   * this type is what stops it being decided by accident.
   *
   * ⚠️ **NOT A SECOND COPY OF {@link unmatchedLines}.** One is work that landed
   * in no cycle column; this is work that could not be put into one unit. A line
   * can be neither, either or both, and adding them reports a smaller gap than
   * the report has.
   */
  unpricedLines: number | null;
  /**
   * The measure to LABEL the figures with, from the `meta.measure` echo via
   * `measureOfResponse` — never from the `measure` that was sent.
   */
  taxMeasure: RevenueTaxMeasure;
  reload: () => void;
}

/**
 * @param search the company term, applied by the server and debounced here.
 * @param baseCurrency the unit to CONVERT every figure into, or `null` to ask
 *   for the old unconverted, currency-grouped rows. ⚠️ **Passing it is a
 *   request, never a guarantee** — read {@link RevenueProductsState.convertedTo}
 *   to find out what actually came back.
 */
export function useRevenueProducts(
  search: string,
  baseCurrency: string | null,
): RevenueProductsState {
  const [rows, setRows] = useState<RevenueProductRow[]>([]);
  const [cycles, setCycles] = useState<RevenueProductCycle[]>([]);
  const [unmatchedLines, setUnmatched] = useState(0);
  const [convertedTo, setConvertedTo] = useState<string | null>(null);
  const [unpricedLines, setUnpriced] = useState<number | null>(null);
  const [taxMeasure, setTaxMeasure] = useState<RevenueTaxMeasure>(REQUESTED_TAX_MEASURE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState(0);

  /*
   * 🔴 **THE DEBOUNCE IS KEYED ON THE TERM HAVING CHANGED, NOT ON "IS THIS THE
   * FIRST READ".** ⚠️ *SUPERSEDED 2026-09-14: a `firstRun` ref, on the reasoning
   * that "every later read is a term somebody is still typing".* That stopped
   * being true the day the base currency joined the request: a currency press
   * and a retry are both later reads and neither is typing, and making them wait
   * 300ms puts skeletons on screen after a deliberate click, which reads as a
   * control that did not take. The term is the only input that arrives a
   * character at a time, so it is the only one that earns the wait.
   */
  const lastTerm = useRef<string | null>(null);

  const reload = useCallback(() => setToken((n) => n + 1), []);

  const term = search.trim();

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const typing = lastTerm.current !== null && lastTerm.current !== term;
    lastTerm.current = term;
    const wait = typing ? DEBOUNCE_MS : 0;

    const timer = setTimeout(() => {
      void (async () => {
        setLoading(true);
        setError(null);
        try {
          /*
           * ⚠️ **Built key by key, never spread — the query schema is
           * `.strict()` and one extra key is a 422 on the only read this tab
           * has.** A conditional spread would also turn off excess-property
           * checking, which is how a key nobody declared reaches the wire over a
           * green build; the declared type is the guard and a spread discards
           * it.
           */
          const query: RevenueProductQuery = {};
          if (term !== '') query.search = term;
          if (baseCurrency !== null) query.baseCurrency = baseCurrency;
          /* Owner decision 2026-10-16: every figure on this tab is cash
           * collected INCLUDING tax. Labelled from the echo below. */
          query.measure = REQUESTED_TAX_MEASURE;

          const answer = await api.listRevenueTrackerProducts(query, controller.signal);
          if (cancelled) return;

          setRows(answer.data);
          setUnmatched(answer.meta?.unmatchedLines ?? 0);
          /*
           * 🔴 **READ FROM THE RESPONSE, NEVER SET FROM `baseCurrency`.** These
           * two lines are the whole guard against labelling unconverted money:
           * an older server ignores the key and answers `200`, so the only
           * difference between "measured in GBP" and "answered in whatever it
           * felt like" is this echo being present.
           *
           * ⚠️ **`?? null` rather than `?? baseCurrency`** — the absence is the
           * finding, and defaulting it to what was asked for erases exactly the
           * state this field exists to report. `undefined` (no such key on this
           * server) and `null` (nothing was converted) are folded because both
           * mean *there is no conversion to label*.
           */
          setConvertedTo(answer.meta?.measuredInCurrency ?? null);
          /*
           * ⛔ **`?? null`, AND NEVER `?? 0`.** `null` means the server examined
           * nothing; `0` means it examined everything and lost none of it. On the
           * one field whose job is to say a revenue report is SHORT, coalescing
           * the first into the second is a silent all-clear. The `undefined` of
           * an older server is the same "did not look", so it folds here and
           * nowhere further down.
           */
          setUnpriced(answer.meta?.unpricedLines ?? null);
          /* 🔴 From the echo: a present `meta.measure` other than `inclTax`
           * labels the tab ex-tax, so the label never claims tax it lacks. */
          setTaxMeasure(measureOfResponse(answer.meta?.measure));
          setCycles(
            [...(answer.meta?.cycles ?? [])].sort((a, b) =>
              a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0,
            ),
          );
        } catch (cause) {
          if (cancelled || controller.signal.aborted) return;
          setError(
            cause instanceof ApiError
              ? cause.message
              : 'The revenue figures could not be loaded.',
          );
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, wait);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
    /* ⚠️ `baseCurrency` is a REQUEST parameter, so it belongs here — it changes
     * what the server computes, not how the answer is read. Leaving it out would
     * leave the figures in the unit of whichever base was asked for last, with a
     * control reading the new one above them. */
  }, [term, token, baseCurrency]);

  return {
    rows,
    cycles,
    loading,
    error,
    unmatchedLines,
    convertedTo,
    unpricedLines,
    taxMeasure,
    reload,
  };
}
